import { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";

// ------------------------------------------------------------
// Data (properties, locations, stats)
// ------------------------------------------------------------
const properties = [
  {
    id: 1,
    name: "Burj Vista Residence",
    location: "Downtown Dubai",
    price: "AED 8.5M",
    type: "Luxury Apartment",
    beds: 4,
    baths: 4,
    area: "3,200 sq.ft",
    tag: "Burj View",
    tagColor: "#B8922A",
    img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&q=80",
    category: "Apartments",
    description:
      "Experience unparalleled luxury in this stunning Burj Vista residence with breathtaking views of the Burj Khalifa. This exquisite apartment features floor-to-ceiling windows, premium finishes, and world-class amenities including a state-of-the-art gym, infinity pool, and 24-hour concierge service.",
    amenities: [
      "Infinity Pool",
      "State-of-the-art Gym",
      "24/7 Concierge",
      "Valet Parking",
      "Security",
      "Children's Play Area",
    ],
    completionYear: 2024,
  },
  {
    id: 2,
    name: "Palm Shore Villa",
    location: "Palm Jumeirah",
    price: "AED 22M",
    type: "Signature Villa",
    beds: 6,
    baths: 7,
    area: "7,800 sq.ft",
    tag: "Beachfront",
    tagColor: "#1A6B55",
    img: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80",
    category: "Villas",
    description:
      "This magnificent beachfront villa on Palm Jumeirah offers direct access to the pristine shores of the Arabian Gulf. The property features spacious living areas, a private infinity pool, landscaped gardens, and panoramic sea views from every room.",
    amenities: [
      "Private Beach Access",
      "Infinity Pool",
      "Landscaped Gardens",
      "Home Cinema",
      "Wine Cellar",
      "Staff Quarters",
    ],
    completionYear: 2023,
  },
  {
    id: 3,
    name: "Marina Sky Penthouse",
    location: "Dubai Marina",
    price: "AED 15.2M",
    type: "Sky Penthouse",
    beds: 5,
    baths: 5,
    area: "5,400 sq.ft",
    tag: "Sea View",
    tagColor: "#1A5080",
    img: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&q=80",
    category: "Penthouses",
    description:
      "Perched atop the prestigious Marina Sky Tower, this penthouse offers 360-degree views of the Dubai Marina and the Arabian Gulf. The residence features a private rooftop terrace with jacuzzi, expansive living areas, and the finest Italian marble finishes.",
    amenities: [
      "Private Rooftop",
      "Jacuzzi",
      "Italian Marble",
      "Smart Home",
      "Private Elevator",
      "Panoramic Views",
    ],
    completionYear: 2024,
  },
  {
    id: 4,
    name: "Creek Horizon Tower",
    location: "Dubai Creek Harbour",
    price: "AED 4.8M",
    type: "Premium Apartment",
    beds: 3,
    baths: 3,
    area: "2,100 sq.ft",
    tag: "New Launch",
    tagColor: "#7A4A1A",
    img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80",
    category: "Apartments",
    description:
      "A stunning new launch at Dubai Creek Harbour with breathtaking views of the Dubai Creek and downtown skyline. This premium apartment offers modern design, energy-efficient systems, and access to world-class retail and dining at the adjacent Dubai Creek Marina.",
    amenities: [
      "Marina Views",
      "Retail Access",
      "Energy Efficient",
      "Swimming Pool",
      "Gym",
      "BBQ Area",
    ],
    completionYear: 2025,
  },
  {
    id: 5,
    name: "Emirates Hills Manor",
    location: "Emirates Hills",
    price: "AED 38M",
    type: "Golf Course Villa",
    beds: 7,
    baths: 8,
    area: "12,500 sq.ft",
    tag: "Ultra Luxury",
    tagColor: "#5A2A7A",
    img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80",
    category: "Villas",
    description:
      "An architectural masterpiece overlooking the lush green fairways of the Emirates Hills Golf Course. This ultra-luxury villa features sweeping curves, floor-to-ceiling glass, a private spa, indoor/outdoor entertaining spaces, and the finest luxury finishes available in Dubai.",
    amenities: [
      "Golf Course Views",
      "Private Spa",
      "Home Theater",
      "Tennis Court",
      "Swimming Pool",
      "Guest House",
    ],
    completionYear: 2023,
  },
  {
    id: 6,
    name: "Jumeirah Bay Island",
    location: "Jumeirah Bay",
    price: "AED 11.4M",
    type: "Waterfront Villa",
    beds: 5,
    baths: 6,
    area: "6,200 sq.ft",
    tag: "Off-Plan",
    tagColor: "#1A6A7A",
    img: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=600&q=80",
    category: "Off-Plan",
    description:
      "An exclusive off-plan waterfront villa on Jumeirah Bay Island with stunning sea views. Designed by renowned architects, this villa features a private jetty, infinity pool, and spacious living areas perfect for modern family living.",
    amenities: [
      "Private Jetty",
      "Infinity Pool",
      "Architect Design",
      "Smart Home",
      "Beach Access",
      "Community Park",
    ],
    completionYear: 2026,
  },
];

const locations = [
  {
    name: "Palm Jumeirah",
    count: "142 PROPERTIES",
    featured: true,
    img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80",
  },
  {
    name: "Downtown Dubai",
    count: "98 PROPERTIES",
    img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80",
  },
  {
    name: "Dubai Marina",
    count: "87 PROPERTIES",
    img: "https://images.unsplash.com/photo-1582407947304-fd86f28320c5?w=600&q=80",
  },
  {
    name: "Business Bay",
    count: "73 PROPERTIES",
    img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80",
  },
];

const stats = [
  { value: "4,800+", label: "Properties Listed" },
  { value: "AED 2.4B+", label: "Transactions Closed" },
  { value: "6,200+", label: "Happy Families" },
  { value: "14 Yrs", label: "Dubai Expertise" },
];

// ─── EMAILJS CONFIGURATION ──────────────────────────────────────────────────
const EMAILJS_CONFIG = {
  SERVICE_ID: "service_somu38y",
  TEMPLATE_ID: "template_jvsn8tq",
  PUBLIC_KEY: "nbgiKhgvRrvIMJUfQ",
};

// ─── ICON CLOSE ─────────────────────────────────────────────────────────────
const IconClose = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// ─── ICON CHECK ─────────────────────────────────────────────────────────────
const IconCheck = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#C9A84C"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// ------------------------------------------------------------
// Main Component
// ------------------------------------------------------------
export default function DubaiEstates() {
  // Phone number constants
  const PHONE_NUMBER = "+918584826222";
  const PHONE_DISPLAY = "+91 85848 26222";

  const [activeFilter, setActiveFilter] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [activeType, setActiveType] = useState("Buy");
  const [filteredProperties, setFilteredProperties] = useState(properties);
  const [contactStep, setContactStep] = useState(1);
  const [contactInterest, setContactInterest] = useState("");
  const [contactBeds, setContactBeds] = useState("");
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    interest: "",
    bedrooms: "",
  });

  // ─── PROPERTY DETAIL MODAL STATE ──────────────────────────────────────────
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyModalOpen, setPropertyModalOpen] = useState(false);

  const filters = ["All", "Villas", "Apartments", "Penthouses", "Off-Plan"];
  const types = ["Buy", "Rent", "Off-Plan"];

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
  }, []);

  useEffect(() => {
    let filtered = properties;
    if (activeFilter !== "All")
      filtered = filtered.filter((p) => p.category === activeFilter);
    if (searchInput.trim() !== "") {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchInput.toLowerCase()) ||
          p.location.toLowerCase().includes(searchInput.toLowerCase()),
      );
    }
    setFilteredProperties(filtered);
  }, [activeFilter, searchInput]);

  // ─── PROPERTY DETAIL MODAL FUNCTIONS ──────────────────────────────────────
  const openPropertyDetails = (property) => {
    setSelectedProperty(property);
    setPropertyModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closePropertyDetails = () => {
    setPropertyModalOpen(false);
    document.body.style.overflow = "unset";
    setTimeout(() => setSelectedProperty(null), 300);
  };

  // ─── CONTACT FORM FUNCTIONS ──────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName?.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName?.trim())
      newErrors.lastName = "Last name is required";
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (
      !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\D/g, ""))
    ) {
      newErrors.phone = "Please enter a valid phone number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitContact = async () => {
    if (!validateForm()) {
      setToast("Please fill in all required fields");
      setTimeout(() => setToast(""), 2800);
      return;
    }

    setIsSubmitting(true);

    try {
      const templateParams = {
        interest: contactInterest || "General Inquiry",
        from_name: `${formData.firstName} ${formData.lastName}`.trim(),
        from_email: formData.email.trim(),
        from_phone: formData.phone.trim(),
        bedrooms: contactBeds || "Not specified",
        message: formData.message?.trim() || "No additional message provided",
        date: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY,
      );

      console.log("EmailJS success:", response);
      setSubmitted(true);
      setToast("✅ Thank you! Your request has been submitted successfully.");

      setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
          interest: "",
          bedrooms: "",
        });
        setErrors({});
        setContactStep(1);
        setContactInterest("");
        setContactBeds("");
        setContactModalOpen(false);
        setSubmitted(false);
        setTimeout(() => setToast(""), 2000);
      }, 1500);
    } catch (error) {
      console.error("EmailJS error:", error);
      setIsSubmitting(false);

      const useMailto = window.confirm(
        "❌ Email service is temporarily unavailable.\n\n" +
          "Would you like to send your inquiry directly via your email app instead?\n\n" +
          "✓ Click OK to open your email app with all details pre-filled\n" +
          "✓ Click Cancel to close and try again later",
      );

      if (useMailto) {
        const subject = `Property Inquiry: ${contactInterest || "New Inquiry"} - ${formData.firstName} ${formData.lastName}`;
        const body = `
NEXT.FRONT PROPERTIES - NEW INQUIRY
====================================
Interest: ${contactInterest || "General Inquiry"}
Bedrooms: ${contactBeds || "Not specified"}

CONTACT DETAILS
------------------------------------
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone}

MESSAGE
------------------------------------
${formData.message || "No message provided"}

Submitted: ${new Date().toLocaleString()}
        `.trim();
        window.location.href = `mailto:info@nextfrontproperties.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        setToast("📧 Opening your email app...");
        setTimeout(() => setToast(""), 2800);
        setContactModalOpen(false);
      } else {
        setToast("❌ Submission cancelled. Please try again later.");
        setTimeout(() => setToast(""), 2800);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openContactModal = (interest, propertyName = "") => {
    setContactInterest(
      propertyName ? `${interest} - ${propertyName}` : interest,
    );
    setContactStep(3);
    setContactModalOpen(true);
    document.body.style.overflow = "hidden";
    setSubmitted(false);
    setErrors({});
  };

  const closeContactModal = () => {
    setContactModalOpen(false);
    document.body.style.overflow = "unset";
    setTimeout(() => {
      if (!submitted) {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
          interest: "",
          bedrooms: "",
        });
        setErrors({});
        setContactStep(1);
        setContactInterest("");
        setContactBeds("");
      }
    }, 400);
  };

  const handleInterestSelect = (value) => {
    setContactInterest(value);
    setContactStep(2);
  };

  const handleBedSelect = (value) => {
    setContactBeds(value);
    setContactStep(3);
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${PHONE_NUMBER}`, "_blank");
  };

  return (
    <div style={styles.root}>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent}>
          <p style={styles.heroEyebrow}>
            The City of Gold · Ultra-Premium Real Estate
          </p>
          <h1 style={styles.heroHeadline}>
            Live Above
            <br />
            <span style={styles.heroAccent}>the Skyline</span>
          </h1>
          <p style={styles.heroSub}>
            From Burj-view penthouses to Palm Jumeirah beachfront villas —<br />
            Dubai's most coveted addresses, curated for discerning buyers.
          </p>
          <div style={styles.searchBox}>
            <div style={styles.searchTabs}>
              {types.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveType(t)}
                  style={{
                    ...styles.searchTab,
                    ...(activeType === t ? styles.searchTabActive : {}),
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
            <div style={styles.searchRow}>
              <span style={{ fontSize: "17px", opacity: 0.4, flexShrink: 0 }}>
                🔍
              </span>
              <input
                style={styles.searchInput}
                placeholder="Search by area, tower or developer…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <select style={styles.searchSelect}>
                <option>Property Type</option>
                <option>Apartment</option>
                <option>Villa</option>
                <option>Penthouse</option>
                <option>Townhouse</option>
              </select>
              <select style={styles.searchSelect}>
                <option>Budget (AED)</option>
                <option>Under 2M</option>
                <option>2M – 5M</option>
                <option>5M – 15M</option>
                <option>15M+</option>
              </select>
              <button style={styles.searchBtn}>Search</button>
            </div>
          </div>
          <div style={styles.heroStats}>
            {stats.map((st) => (
              <div key={st.label} style={styles.heroStat}>
                <span style={styles.heroStatVal}>{st.value}</span>
                <span style={styles.heroStatLabel}>{st.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={styles.ticker}>
        <div style={styles.tickerTrack}>
          {[...Array(2)].flatMap(() =>
            [
              "Downtown Dubai · AED 8.5M",
              "Palm Jumeirah · AED 22M",
              "Dubai Marina · AED 15.2M",
              "Emirates Hills · AED 38M",
              "Business Bay · AED 4.2M",
              "Creek Harbour · AED 6.8M",
            ].map((item, i) => (
              <span key={item + i} style={styles.tickerItem}>
                <span style={{ color: "rgba(8,18,28,0.45)" }}>◆</span> {item}
              </span>
            )),
          )}
        </div>
      </div>

      <section style={styles.section}>
        <div style={styles.inner}>
          <div style={styles.sectionHead}>
            <div>
              <p style={styles.eyebrow}>Exclusively Curated</p>
              <h2 style={styles.sectionTitle}>Prime Listings</h2>
            </div>
            <div style={styles.filterRow}>
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  style={{
                    ...styles.filterBtn,
                    ...(activeFilter === f ? styles.filterBtnActive : {}),
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          {filteredProperties.length === 0 ? (
            <div style={styles.noResults}>
              No properties match your criteria. Try adjusting filters.
            </div>
          ) : (
            <div style={styles.propGrid}>
              {filteredProperties.map((p) => (
                <div key={p.id} style={styles.propCard}>
                  <div
                    style={{
                      position: "relative",
                      overflow: "hidden",
                      height: "220px",
                    }}
                  >
                    <img
                      src={p.img}
                      alt={p.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        top: "14px",
                        left: "14px",
                        background: p.tagColor,
                        color: "#fff",
                        fontSize: "11px",
                        fontWeight: 700,
                        padding: "4px 10px",
                        borderRadius: "100px",
                      }}
                    >
                      {p.tag}
                    </span>
                    <button
                      style={{
                        position: "absolute",
                        top: "12px",
                        right: "14px",
                        background: "rgba(255,255,255,0.9)",
                        border: "none",
                        borderRadius: "50%",
                        width: "32px",
                        height: "32px",
                        fontSize: "16px",
                        cursor: "pointer",
                      }}
                    >
                      ♡
                    </button>
                  </div>
                  <div style={{ padding: "20px" }}>
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#C4974A",
                        fontWeight: 700,
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        marginBottom: "6px",
                      }}
                    >
                      {p.type}
                    </p>
                    <h3
                      style={{
                        fontSize: "19px",
                        fontFamily: "Georgia, serif",
                        fontWeight: 700,
                        color: "#F5F0E8",
                        margin: "0 0 6px",
                      }}
                    >
                      {p.name}
                    </h3>
                    <p
                      style={{
                        fontSize: "13px",
                        color: "rgba(245,240,232,0.45)",
                        marginBottom: "14px",
                      }}
                    >
                      📍 {p.location}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        gap: "14px",
                        marginBottom: "16px",
                        paddingBottom: "16px",
                        borderBottom: "1px solid rgba(196,151,74,0.12)",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          color: "rgba(245,240,232,0.45)",
                        }}
                      >
                        🛏 {p.beds} Beds
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "rgba(245,240,232,0.45)",
                        }}
                      >
                        🚿 {p.baths} Baths
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "rgba(245,240,232,0.45)",
                        }}
                      >
                        📐 {p.area}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "Georgia, serif",
                          fontSize: "20px",
                          fontWeight: 700,
                          color: "#F5F0E8",
                        }}
                      >
                        {p.price}
                      </span>
                      <button
                        style={styles.detailBtn}
                        onClick={() => openPropertyDetails(p)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ textAlign: "center", marginTop: "48px" }}>
            <button style={styles.outlineBtn}>View All Properties →</button>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section style={{ ...styles.section, background: "#07101e" }}>
        <div style={styles.inner}>
          <div style={styles.whoGrid}>
            <div style={styles.whoImageWrap}>
              <div style={styles.whoCornerTopRight} />
              <img
                src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=700&q=80"
                alt="Luxury interior"
                style={styles.whoImg}
              />
              <div style={styles.whoCornerBottomLeft} />
              <div style={styles.whoBadge}>
                <span style={styles.whoBadgeNum}>14</span>
                <span style={styles.whoBadgeLabel}>
                  YEARS OF
                  <br />
                  EXCELLENCE
                </span>
              </div>
            </div>
            <div style={styles.whoRight}>
              <div style={styles.whoEyebrowRow}>
                <span style={styles.whoEyebrowLine} />
                <span style={styles.whoEyebrowText}>WHO WE ARE</span>
              </div>
              <h2 style={styles.whoHeading}>
                REDEFINING LUXURY
                <br />
                <span style={{ color: "#C4974A" }}>SINCE 2010</span>
              </h2>
              <p style={styles.whoBody}>
                A bespoke experience is what you can look forward to with
                DubaiEstates. We are specialists in luxury real estate with a
                property portfolio that only features Dubai's finest, most
                luxurious homes in prime locations across the city.
              </p>
              <p style={styles.whoBody}>
                As experienced professionals, we aspire to deliver a bespoke
                experience to every client. Through strategic partnerships we
                bring you value-added services including property management,
                mortgage assistance, and interior design.
              </p>
              <button
                style={styles.goldBtn}
                onClick={() => openContactModal("Consultation")}
              >
                DISCOVER MORE
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section
        style={{ ...styles.section, background: "#07101e", paddingTop: "72px" }}
      >
        <div style={styles.inner}>
          <div style={styles.locationsHead}>
            <div style={styles.whoEyebrowRow}>
              <span style={styles.whoEyebrowLine} />
              <span style={styles.whoEyebrowText}>PRIME LOCATIONS</span>
            </div>
            <h2 style={styles.locationsTitle}>
              EXPLORE <span style={{ color: "#C4974A" }}>DUBAI'S FINEST</span>
            </h2>
          </div>
          <div style={styles.locGrid}>
            <div style={styles.locCardFeatured}>
              <img
                src={locations[0].img}
                alt={locations[0].name}
                style={styles.locImg}
              />
              <div style={styles.locOverlay} />
              <div style={styles.locLabel}>
                <h3 style={styles.locName}>{locations[0].name}</h3>
                <p style={styles.locCount}>{locations[0].count}</p>
              </div>
            </div>
            <div style={styles.locRight}>
              {locations.slice(1).map((loc) => (
                <div key={loc.name} style={styles.locCard}>
                  <img src={loc.img} alt={loc.name} style={styles.locImg} />
                  <div style={styles.locOverlay} />
                  <div style={styles.locLabel}>
                    <h3 style={styles.locName}>{loc.name}</h3>
                    <p style={styles.locCount}>{loc.count}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section style={{ ...styles.section, background: "#07101e" }}>
        <div style={styles.inner}>
          <p style={styles.eyebrow}>How It Works</p>
          <h2 style={styles.sectionTitle}>Your Journey to Ownership</h2>
          <div style={styles.processGrid}>
            {[
              {
                n: "01",
                title: "Discover",
                desc: "Browse 4,800+ verified listings across Dubai's most sought-after communities.",
              },
              {
                n: "02",
                title: "Consult",
                desc: "A dedicated advisor walks you through every option — no pressure, full clarity.",
              },
              {
                n: "03",
                title: "View",
                desc: "In-person or virtual tours arranged within 24 hours, 7 days a week.",
              },
              {
                n: "04",
                title: "Close",
                desc: "End-to-end support from DLD registration to key handover. Zero surprises.",
              },
            ].map((step) => (
              <div key={step.n} style={styles.processStep}>
                <span style={styles.stepNum}>{step.n}</span>
                <h4 style={styles.stepTitle}>{step.title}</h4>
                <p style={styles.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section style={{ ...styles.section, background: "#07101e" }}>
        <div style={styles.inner}>
          <div style={styles.whyGrid}>
            <div>
              <p style={styles.eyebrow}>Why DubaiEstates</p>
              <h2 style={{ ...styles.sectionTitle, lineHeight: 1.2 }}>
                Dubai's Most
                <br />
                Trusted Property
                <br />
                <span style={{ color: "#C4974A" }}>Specialists</span>
              </h2>
              <p style={styles.whyBody}>
                Since 2010, we have guided over 6,200 buyers and investors to
                the right address across Dubai's most prestigious communities.
                Our advisors combine deep market knowledge with complete
                transparency — so every decision you make is an informed one.
              </p>
              <button
                style={styles.goldBtn}
                onClick={() => openContactModal("Advisor Consultation")}
              >
                Speak to an Advisor
              </button>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "28px" }}
            >
              {[
                {
                  icon: "🏙",
                  title: "RERA Registered Brokers",
                  desc: "All agents hold valid RERA licences. Every listing is fully verified and DLD-compliant.",
                },
                {
                  icon: "📊",
                  title: "Market Intelligence",
                  desc: "Real-time data on price per sq.ft, ROI benchmarks, and demand trends across all 22 zones.",
                },
                {
                  icon: "🌍",
                  title: "Global Buyer Network",
                  desc: "We serve buyers from 60+ countries. Multilingual advisors available round the clock.",
                },
                {
                  icon: "🔑",
                  title: "Key-to-Key Service",
                  desc: "From initial search to DLD transfer and snagging — we stay with you until you hold the keys.",
                },
              ].map((w) => (
                <div
                  key={w.title}
                  style={{
                    display: "flex",
                    gap: "18px",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{ fontSize: "26px", lineHeight: 1, flexShrink: 0 }}
                  >
                    {w.icon}
                  </span>
                  <div>
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#F5F0E8",
                        margin: "0 0 4px",
                      }}
                    >
                      {w.title}
                    </h4>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "rgba(245,240,232,0.5)",
                        lineHeight: 1.65,
                        margin: 0,
                      }}
                    >
                      {w.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        style={{ ...styles.section, background: "#07101e", paddingTop: "0" }}
      >
        <div style={styles.inner}>
          <p style={styles.eyebrow}>Client Voices</p>
          <h2 style={styles.sectionTitle}>Stories from Homeowners</h2>
          <div style={styles.testiGrid}>
            {[
              {
                name: "Khalid Al Mansoori",
                loc: "Bought in Emirates Hills",
                text: "The team understood exactly what we needed and found us a villa that exceeded every expectation.",
                stars: 5,
              },
              {
                name: "Sarah Thompson",
                loc: "Invested in Downtown Dubai",
                text: "As an investor from London, I needed an advisor I could trust completely. The market insights were spot-on.",
                stars: 5,
              },
              {
                name: "Ravi & Meera Kapoor",
                loc: "Penthouse in Dubai Marina",
                text: "We bought entirely remotely from Mumbai. The snagging service at handover was particularly thorough.",
                stars: 5,
              },
            ].map((t) => (
              <div key={t.name} style={styles.testiCard}>
                <div
                  style={{
                    color: "#C4974A",
                    fontSize: "18px",
                    marginBottom: "14px",
                  }}
                >
                  {"★".repeat(t.stars)}
                </div>
                <p
                  style={{
                    fontSize: "15px",
                    color: "rgba(245,240,232,0.65)",
                    lineHeight: 1.75,
                    marginBottom: "20px",
                    fontStyle: "italic",
                  }}
                >
                  "{t.text}"
                </p>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "14px" }}
                >
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "50%",
                      background: "#C4974A",
                      color: "#08121C",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {t.name[0]}
                  </div>
                  <div>
                    <strong
                      style={{
                        display: "block",
                        color: "#F5F0E8",
                        fontSize: "15px",
                      }}
                    >
                      {t.name}
                    </strong>
                    <span
                      style={{
                        color: "rgba(245,240,232,0.4)",
                        fontSize: "13px",
                      }}
                    >
                      {t.loc}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaBanner}>
        <div style={styles.ctaInner}>
          <p
            style={{
              color: "#C4974A",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "3px",
              textTransform: "uppercase",
              marginBottom: "16px",
            }}
          >
            Your Next Move
          </p>
          <h2 style={styles.ctaTitle}>Ready to Own a Piece of Dubai?</h2>
          <p style={styles.ctaSub}>
            Book a free consultation with our property specialists. Zero
            obligation — pure clarity.
          </p>
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              style={styles.goldBtn}
              onClick={() => openContactModal("Free Consultation")}
            >
              Book a Free Call
            </button>
            <button
              style={{
                ...styles.outlineBtn,
                borderColor: "rgba(255,255,255,0.3)",
                color: "#F5F0E8",
              }}
            >
              Browse All Listings
            </button>
          </div>
        </div>
      </section>

      {/* ─── PROPERTY DETAIL MODAL ── */}
      {propertyModalOpen && selectedProperty && (
        <div
          className="ke-property-modal-overlay"
          onClick={closePropertyDetails}
        >
          <div
            className="ke-property-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="ke-property-modal-close"
              onClick={closePropertyDetails}
            >
              <IconClose />
            </button>

            <div className="ke-property-modal-body">
              <div className="ke-property-modal-image">
                <img src={selectedProperty.img} alt={selectedProperty.name} />
                <span
                  className="ke-property-modal-tag"
                  style={{ background: selectedProperty.tagColor }}
                >
                  {selectedProperty.tag}
                </span>
              </div>

              <div className="ke-property-modal-info">
                <div className="ke-property-modal-header">
                  <div>
                    <p className="ke-property-modal-type">
                      {selectedProperty.type}
                    </p>
                    <h2 className="ke-property-modal-name">
                      {selectedProperty.name}
                    </h2>
                    <p className="ke-property-modal-location">
                      📍 {selectedProperty.location}
                    </p>
                  </div>
                  <div className="ke-property-modal-price">
                    {selectedProperty.price}
                  </div>
                </div>

                <div className="ke-property-modal-details">
                  <div className="ke-property-modal-detail-item">
                    <span>🛏</span>
                    <span>{selectedProperty.beds} Beds</span>
                  </div>
                  <div className="ke-property-modal-detail-item">
                    <span>🚿</span>
                    <span>{selectedProperty.baths} Baths</span>
                  </div>
                  <div className="ke-property-modal-detail-item">
                    <span>📐</span>
                    <span>{selectedProperty.area}</span>
                  </div>
                  <div className="ke-property-modal-detail-item">
                    <span>📅</span>
                    <span>
                      Complete: {selectedProperty.completionYear || "2025"}
                    </span>
                  </div>
                </div>

                <div className="ke-property-modal-description">
                  <h4>About This Property</h4>
                  <p>{selectedProperty.description}</p>
                </div>

                <div className="ke-property-modal-amenities">
                  <h4>Key Amenities</h4>
                  <div className="ke-property-modal-amenities-grid">
                    {selectedProperty.amenities.map((amenity, index) => (
                      <span key={index} className="ke-property-modal-amenity">
                        <IconCheck /> {amenity}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="ke-property-modal-actions">
                  <button
                    className="ke-property-modal-btn-primary"
                    onClick={() => {
                      closePropertyDetails();
                      setTimeout(
                        () =>
                          openContactModal(
                            "Property Inquiry",
                            selectedProperty.name,
                          ),
                        300,
                      );
                    }}
                  >
                    Inquire Now
                  </button>
                  <button
                    className="ke-property-modal-btn-secondary"
                    onClick={() => {
                      window.open(
                        `https://wa.me/${PHONE_NUMBER}?text=Hi%2C%20I'm%20interested%20in%20${encodeURIComponent(selectedProperty.name)}`,
                        "_blank",
                      );
                    }}
                  >
                    WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── CONTACT MODAL ── */}
      {contactModalOpen && (
        <div className="ke-contact-modal-overlay" onClick={closeContactModal}>
          <div
            className="ke-contact-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="ke-contact-modal-close"
              onClick={closeContactModal}
            >
              <IconClose />
            </button>

            {!submitted && (
              <>
                <div className="ke-contact-steps">
                  <div
                    className={`ke-contact-step-dot ${contactStep >= 1 ? "active" : ""}`}
                  />
                  <div
                    className={`ke-contact-step-dot ${contactStep >= 2 ? "active" : ""}`}
                  />
                  <div
                    className={`ke-contact-step-dot ${contactStep >= 3 ? "active" : ""}`}
                  />
                </div>

                {contactStep === 1 && (
                  <div>
                    <h3
                      style={{
                        fontFamily: "Georgia, serif",
                        fontSize: 22,
                        color: "#f0e8d8",
                        marginBottom: 6,
                        textAlign: "center",
                      }}
                    >
                      Express Your Interest
                    </h3>
                    <p
                      style={{
                        fontSize: 13,
                        color: "rgba(232,224,208,0.5)",
                        textAlign: "center",
                        marginBottom: 24,
                      }}
                    >
                      Select what you're looking for
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      {[
                        { icon: "🏠", label: "Buy/Sell" },
                        { icon: "🔑", label: "Rent" },
                        { icon: "📊", label: "Invest" },
                        { icon: "💰", label: "Mortgage" },
                        { icon: "🏗️", label: "Off-Plan" },
                        { icon: "📋", label: "General" },
                      ].map((opt) => (
                        <button
                          key={opt.label}
                          onClick={() => handleInterestSelect(opt.label)}
                          style={{
                            padding: "16px",
                            border: `1px solid ${contactInterest === opt.label ? "#C9A84C" : "rgba(201,168,76,0.14)"}`,
                            borderRadius: "8px",
                            background:
                              contactInterest === opt.label
                                ? "rgba(201,168,76,0.1)"
                                : "transparent",
                            color: "#e8e0d0",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            textAlign: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "28px",
                              display: "block",
                              marginBottom: "6px",
                            }}
                          >
                            {opt.icon}
                          </span>
                          <span style={{ fontSize: "12px", fontWeight: 600 }}>
                            {opt.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {contactStep === 2 && (
                  <div>
                    <h3
                      style={{
                        fontFamily: "Georgia, serif",
                        fontSize: 22,
                        color: "#f0e8d8",
                        marginBottom: 6,
                        textAlign: "center",
                      }}
                    >
                      How Many Bedrooms?
                    </h3>
                    <p
                      style={{
                        fontSize: 13,
                        color: "rgba(232,224,208,0.5)",
                        textAlign: "center",
                        marginBottom: 24,
                      }}
                    >
                      Select your preference for {contactInterest}
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5,1fr)",
                        gap: "8px",
                      }}
                    >
                      {["1", "2", "3", "4", "5+"].map((b) => (
                        <button
                          key={b}
                          onClick={() => handleBedSelect(b)}
                          style={{
                            padding: "14px",
                            border: `1px solid ${contactBeds === b ? "#C9A84C" : "rgba(201,168,76,0.14)"}`,
                            borderRadius: "8px",
                            background:
                              contactBeds === b
                                ? "rgba(201,168,76,0.1)"
                                : "transparent",
                            color: "#e8e0d0",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            textAlign: "center",
                            fontSize: "18px",
                            fontWeight: 600,
                          }}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                    <button
                      style={{
                        background: "transparent",
                        color: "#C9A84C",
                        border: "1px solid rgba(201,168,76,0.3)",
                        padding: "12px 24px",
                        fontSize: "12px",
                        fontWeight: 600,
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        cursor: "pointer",
                        borderRadius: "6px",
                        width: "100%",
                        justifyContent: "center",
                        marginTop: "16px",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => setContactStep(1)}
                    >
                      ← Back
                    </button>
                  </div>
                )}

                {contactStep === 3 && (
                  <div>
                    <h3
                      style={{
                        fontFamily: "Georgia, serif",
                        fontSize: 22,
                        color: "#f0e8d8",
                        marginBottom: 6,
                        textAlign: "center",
                      }}
                    >
                      Your Details
                    </h3>
                    <p
                      style={{
                        fontSize: 13,
                        color: "rgba(232,224,208,0.5)",
                        textAlign: "center",
                        marginBottom: 20,
                      }}
                    >
                      We'll reach out within 24 hours
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <input
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: `1px solid ${errors.firstName ? "#ff6b6b" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: "6px",
                          padding: "11px 14px",
                          fontSize: "13px",
                          color: "#e8e0d0",
                          fontFamily: "'DM Sans', sans-serif",
                          outline: "none",
                          transition: "border-color 0.2s",
                          width: "100%",
                        }}
                        placeholder="First Name*"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                      />
                      <input
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: `1px solid ${errors.lastName ? "#ff6b6b" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: "6px",
                          padding: "11px 14px",
                          fontSize: "13px",
                          color: "#e8e0d0",
                          fontFamily: "'DM Sans', sans-serif",
                          outline: "none",
                          transition: "border-color 0.2s",
                          width: "100%",
                        }}
                        placeholder="Last Name*"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                      />
                    </div>
                    <input
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: `1px solid ${errors.email ? "#ff6b6b" : "rgba(255,255,255,0.08)"}`,
                        borderRadius: "6px",
                        padding: "11px 14px",
                        fontSize: "13px",
                        color: "#e8e0d0",
                        fontFamily: "'DM Sans', sans-serif",
                        outline: "none",
                        transition: "border-color 0.2s",
                        width: "100%",
                        marginBottom: "10px",
                      }}
                      placeholder="Email Address*"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                    <input
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: `1px solid ${errors.phone ? "#ff6b6b" : "rgba(255,255,255,0.08)"}`,
                        borderRadius: "6px",
                        padding: "11px 14px",
                        fontSize: "13px",
                        color: "#e8e0d0",
                        fontFamily: "'DM Sans', sans-serif",
                        outline: "none",
                        transition: "border-color 0.2s",
                        width: "100%",
                        marginBottom: "10px",
                      }}
                      placeholder="Phone Number*"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                    <textarea
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "6px",
                        padding: "11px 14px",
                        fontSize: "13px",
                        color: "#e8e0d0",
                        fontFamily: "'DM Sans', sans-serif",
                        outline: "none",
                        transition: "border-color 0.2s",
                        width: "100%",
                        resize: "vertical",
                        marginBottom: "10px",
                      }}
                      placeholder="Your message (optional)..."
                      rows={2}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                    />
                    <div
                      style={{
                        background: "rgba(201,168,76,0.08)",
                        border: "1px solid rgba(201,168,76,0.14)",
                        padding: "10px 14px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        color: "rgba(232,224,208,0.5)",
                        marginBottom: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <span>Interest:</span>
                        <span style={{ color: "#C9A84C", fontWeight: 600 }}>
                          {contactInterest}
                        </span>
                      </div>
                      {contactBeds && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>Bedrooms:</span>
                          <span style={{ color: "#C9A84C", fontWeight: 600 }}>
                            {contactBeds}
                          </span>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                      }}
                    >
                      <button
                        style={{
                          background: "#C9A84C",
                          color: "#07101e",
                          border: "none",
                          padding: "12px 24px",
                          fontSize: "12px",
                          fontWeight: 700,
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          borderRadius: "6px",
                          flex: 1,
                          justifyContent: "center",
                          opacity: isSubmitting ? 0.7 : 1,
                          transition: "all 0.3s ease",
                        }}
                        onClick={handleSubmitContact}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "SENDING..." : "SUBMIT"}
                      </button>
                      <button
                        style={{
                          background: "transparent",
                          color: "#C9A84C",
                          border: "1px solid rgba(201,168,76,0.3)",
                          padding: "12px 20px",
                          fontSize: "12px",
                          fontWeight: 600,
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          borderRadius: "6px",
                          transition: "all 0.3s ease",
                        }}
                        onClick={() => setContactStep(2)}
                      >
                        ← Back
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {submitted && (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <h3
                  style={{
                    fontFamily: "Georgia, serif",
                    fontSize: 24,
                    color: "#f0e8d8",
                    marginBottom: 8,
                  }}
                >
                  Thank You!
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    color: "rgba(232,224,208,0.55)",
                    lineHeight: 1.7,
                  }}
                >
                  Our team will contact you within 24 hours to discuss your{" "}
                  {contactInterest} requirements.
                </p>
                <button
                  style={{
                    background: "#C9A84C",
                    color: "#07101e",
                    border: "none",
                    padding: "12px 28px",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    borderRadius: "6px",
                    marginTop: "24px",
                    transition: "all 0.3s ease",
                  }}
                  onClick={() => {
                    setSubmitted(false);
                    setContactStep(1);
                    setContactInterest("");
                    setContactBeds("");
                    setFormData({
                      firstName: "",
                      lastName: "",
                      email: "",
                      phone: "",
                      message: "",
                      interest: "",
                      bedrooms: "",
                    });
                    setErrors({});
                    setContactModalOpen(false);
                    document.body.style.overflow = "unset";
                  }}
                >
                  CLOSE
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TOAST NOTIFICATION ── */}
      {toast && (
        <div className="ke-toast" id="ke-toast">
          {toast}
        </div>
      )}

      {/* ─── STYLES ── */}
      <style>{`
        .ke-toast {
          position: fixed;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          background: #0d1a2d;
          border: 1px solid #C9A84C;
          color: #e8e0d0;
          padding: 14px 28px;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.5px;
          z-index: 99999;
          display: flex;
          align-items: center;
          gap: 10px;
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
          animation: toastIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          backdrop-filter: blur(10px);
        }

        @keyframes toastIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0) scale(1);
          }
        }

        @media (max-width: 480px) {
          .ke-toast {
            font-size: 12px;
            padding: 12px 20px;
            bottom: 24px;
            white-space: normal;
            max-width: 90%;
            text-align: center;
          }
        }

        /* ── PROPERTY MODAL STYLES ── */
        .ke-property-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.92);
          backdrop-filter: blur(16px);
          z-index: 9998;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        .ke-property-modal-content {
          background: linear-gradient(145deg, #0d1a2d 0%, #07101e 100%);
          border: 1px solid rgba(201,168,76,0.2);
          border-radius: 16px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
        }

        .ke-property-modal-content::-webkit-scrollbar {
          width: 4px;
        }
        .ke-property-modal-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .ke-property-modal-content::-webkit-scrollbar-thumb {
          background: #C9A84C;
          border-radius: 2px;
        }

        .ke-property-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
        }

        .ke-property-modal-close:hover {
          background: rgba(201,168,76,0.2);
          border-color: #C9A84C;
          transform: rotate(90deg);
        }

        .ke-property-modal-close svg {
          width: 20px;
          height: 20px;
        }

        .ke-property-modal-body {
          display: flex;
          flex-direction: column;
        }

        .ke-property-modal-image {
          position: relative;
          width: 100%;
          height: 300px;
          overflow: hidden;
          border-radius: 16px 16px 0 0;
        }

        .ke-property-modal-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .ke-property-modal-tag {
          position: absolute;
          top: 16px;
          left: 16px;
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          padding: 6px 14px;
          border-radius: 100px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .ke-property-modal-info {
          padding: 28px 32px 32px;
        }

        .ke-property-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .ke-property-modal-type {
          font-size: 11px;
          color: #C9A84C;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          margin: 0 0 4px;
        }

        .ke-property-modal-name {
          font-family: Georgia, serif;
          font-size: 24px;
          font-weight: 700;
          color: #f0e8d8;
          margin: 0 0 4px;
        }

        .ke-property-modal-location {
          font-size: 14px;
          color: rgba(232,224,208,0.5);
          margin: 0;
        }

        .ke-property-modal-price {
          font-family: Georgia, serif;
          font-size: 26px;
          font-weight: 700;
          color: #C9A84C;
          white-space: nowrap;
        }

        .ke-property-modal-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 12px;
          padding: 16px 0;
          border-top: 1px solid rgba(201,168,76,0.1);
          border-bottom: 1px solid rgba(201,168,76,0.1);
          margin-bottom: 20px;
        }

        .ke-property-modal-detail-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: rgba(232,224,208,0.6);
        }

        .ke-property-modal-detail-item span:first-child {
          font-size: 18px;
        }

        .ke-property-modal-description {
          margin-bottom: 20px;
        }

        .ke-property-modal-description h4 {
          font-size: 16px;
          font-weight: 700;
          color: #f0e8d8;
          margin: 0 0 8px;
        }

        .ke-property-modal-description p {
          font-size: 14px;
          color: rgba(232,224,208,0.6);
          line-height: 1.8;
          margin: 0;
        }

        .ke-property-modal-amenities {
          margin-bottom: 24px;
        }

        .ke-property-modal-amenities h4 {
          font-size: 16px;
          font-weight: 700;
          color: #f0e8d8;
          margin: 0 0 12px;
        }

        .ke-property-modal-amenities-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 8px;
        }

        .ke-property-modal-amenity {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: rgba(232,224,208,0.6);
        }

        .ke-property-modal-amenity svg {
          flex-shrink: 0;
        }

        .ke-property-modal-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .ke-property-modal-btn-primary {
          background: #C9A84C;
          color: #07101e;
          border: none;
          padding: 12px 32px;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: 6px;
          flex: 1;
          min-width: 140px;
          transition: all 0.3s ease;
        }

        .ke-property-modal-btn-primary:hover {
          background: #d8b85a;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(201,168,76,0.3);
        }

        .ke-property-modal-btn-secondary {
          background: transparent;
          color: #f0e8d8;
          border: 1px solid rgba(201,168,76,0.3);
          padding: 12px 32px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border-radius: 6px;
          flex: 1;
          min-width: 140px;
          transition: all 0.3s ease;
        }

        .ke-property-modal-btn-secondary:hover {
          background: rgba(201,168,76,0.1);
          border-color: #C9A84C;
        }

        /* ── CONTACT MODAL STYLES ── */
        .ke-contact-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.92);
          backdrop-filter: blur(12px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .ke-contact-modal-content {
          background: linear-gradient(145deg, #0d1a2d 0%, #07101e 100%);
          border: 1px solid rgba(201,168,76,0.2);
          border-radius: 16px;
          max-width: 560px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
          padding: 40px 36px;
        }

        .ke-contact-modal-content::-webkit-scrollbar {
          width: 4px;
        }
        .ke-contact-modal-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .ke-contact-modal-content::-webkit-scrollbar-thumb {
          background: #C9A84C;
          border-radius: 2px;
        }

        .ke-contact-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
        }

        .ke-contact-modal-close:hover {
          background: rgba(201,168,76,0.2);
          border-color: #C9A84C;
          transform: rotate(90deg);
        }

        .ke-contact-modal-close svg {
          width: 20px;
          height: 20px;
        }

        .ke-contact-steps {
          display: flex;
          gap: 8px;
          margin-bottom: 28px;
          justify-content: center;
        }

        .ke-contact-step-dot {
          width: 30px;
          height: 3px;
          border-radius: 2px;
          background: rgba(201,168,76,0.14);
          transition: background 0.3s;
        }

        .ke-contact-step-dot.active {
          background: #C9A84C;
        }

        /* ── RESPONSIVE FOR MODALS ── */
        @media (max-width: 768px) {
          .ke-property-modal-content {
            max-width: 100%;
            margin: 10px;
            border-radius: 12px;
          }

          .ke-property-modal-image {
            height: 200px;
          }

          .ke-property-modal-info {
            padding: 20px;
          }

          .ke-property-modal-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .ke-property-modal-price {
            font-size: 22px;
          }

          .ke-property-modal-amenities-grid {
            grid-template-columns: 1fr 1fr;
          }

          .ke-property-modal-actions {
            flex-direction: column;
          }

          .ke-property-modal-btn-primary,
          .ke-property-modal-btn-secondary {
            width: 100%;
            text-align: center;
          }

          .ke-contact-modal-content {
            padding: 28px 20px;
            margin: 10px;
          }
        }

        @media (max-width: 480px) {
          .ke-property-modal-image {
            height: 160px;
          }

          .ke-property-modal-name {
            font-size: 20px;
          }

          .ke-property-modal-details {
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .ke-property-modal-amenities-grid {
            grid-template-columns: 1fr;
          }

          .ke-contact-modal-content .ke-contact-steps {
            gap: 6px;
          }
          .ke-contact-modal-content .ke-contact-step-dot {
            width: 24px;
          }
        }
      `}</style>
    </div>
  );
}

// ─── STYLES WITH DARK THEME ──────────────────────────────────────────────
const styles = {
  root: {
    fontFamily: "Inter, -apple-system, sans-serif",
    color: "#e8e0d0",
    background: "#07101e",
    overflowX: "hidden",
  },

  // ── HERO ──
  hero: {
    position: "relative",
    minHeight: "100vh",
    backgroundImage:
      "url('https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1800&q=90')",
    backgroundSize: "cover",
    backgroundPosition: "center 40%",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(7,16,30,0.93) 0%, rgba(13,26,45,0.88) 50%, rgba(7,16,30,0.9) 100%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    maxWidth: "1280px",
    margin: "0 auto",
    padding: "120px 32px 80px",
    width: "100%",
  },
  heroEyebrow: {
    color: "#C9A84C",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "3px",
    textTransform: "uppercase",
    marginBottom: "16px",
  },
  heroHeadline: {
    fontFamily: "Georgia, Times New Roman, serif",
    fontSize: "clamp(50px,7.5vw,90px)",
    fontWeight: 700,
    color: "#f0e8d8",
    lineHeight: 1.05,
    margin: "0 0 22px",
    letterSpacing: "-2.5px",
  },
  heroAccent: { color: "#C9A84C" },
  heroSub: {
    color: "rgba(232,224,208,0.58)",
    fontSize: "clamp(14px,4vw,17px)",
    lineHeight: 1.75,
    marginBottom: "40px",
    fontWeight: 300,
  },
  searchBox: {
    background: "rgba(255,255,255,0.97)",
    borderRadius: "14px",
    maxWidth: "880px",
    boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
    overflow: "hidden",
  },
  searchTabs: {
    display: "flex",
    borderBottom: "1px solid #EAE6DE",
  },
  searchTab: {
    flex: 1,
    padding: "14px 0",
    border: "none",
    background: "transparent",
    fontSize: "14px",
    fontWeight: 500,
    color: "#7A8FA8",
    cursor: "pointer",
  },
  searchTabActive: {
    color: "#0A1420",
    fontWeight: 700,
    borderBottom: "2.5px solid #C9A84C",
    background: "#FFFEF9",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    padding: "16px 20px",
    gap: "12px",
    flexWrap: "wrap",
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "15px",
    color: "#0A1420",
    background: "transparent",
    fontFamily: "Inter, sans-serif",
    minWidth: "150px",
  },
  searchSelect: {
    border: "1px solid #E4E0D8",
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "13px",
    color: "#0A1420",
    background: "#fff",
    outline: "none",
    cursor: "pointer",
  },
  searchBtn: {
    background: "#C9A84C",
    color: "#07101e",
    border: "none",
    borderRadius: "8px",
    padding: "10px 28px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
  },
  heroStats: {
    display: "flex",
    gap: "clamp(20px,5vw,48px)",
    marginTop: "44px",
    flexWrap: "wrap",
  },
  heroStat: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  heroStatVal: {
    fontFamily: "Georgia, serif",
    fontSize: "clamp(22px,5vw,28px)",
    fontWeight: 700,
    color: "#C9A84C",
    letterSpacing: "-1px",
  },
  heroStatLabel: {
    fontSize: "11px",
    color: "rgba(232,224,208,0.48)",
    letterSpacing: "1.2px",
    textTransform: "uppercase",
  },

  // ── TICKER ──
  ticker: {
    background: "#C9A84C",
    overflow: "hidden",
    padding: "10px 0",
    whiteSpace: "nowrap",
  },
  tickerTrack: {
    display: "inline-flex",
    animation: "tickerMove 32s linear infinite",
  },
  tickerItem: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#07101e",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    padding: "0 32px",
  },

  // ── SECTION COMMON ──
  section: {
    padding: "clamp(56px,8vw,88px) 0",
    background: "#07101e",
  },
  inner: {
    maxWidth: "1280px",
    margin: "0 auto",
    padding: "0 32px",
  },
  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: "48px",
    flexWrap: "wrap",
    gap: "20px",
  },
  eyebrow: {
    color: "#C9A84C",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "3px",
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  sectionTitle: {
    fontFamily: "Georgia, serif",
    fontSize: "clamp(28px,4.5vw,44px)",
    fontWeight: 700,
    color: "#f0e8d8",
    letterSpacing: "-1.2px",
    lineHeight: 1.15,
    margin: 0,
  },

  // ── FILTERS ──
  filterRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  filterBtn: {
    padding: "8px 18px",
    border: "1.5px solid rgba(201,168,76,0.25)",
    borderRadius: "100px",
    background: "transparent",
    fontSize: "13px",
    color: "rgba(232,224,208,0.5)",
    cursor: "pointer",
    fontWeight: 500,
  },
  filterBtnActive: {
    background: "#C9A84C",
    color: "#07101e",
    borderColor: "#C9A84C",
  },

  // ── PROPERTIES ──
  propGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))",
    gap: "28px",
  },
  propCard: {
    background: "rgba(255,255,255,0.04)",
    borderRadius: "14px",
    overflow: "hidden",
    border: "1px solid rgba(201,168,76,0.15)",
    cursor: "pointer",
  },
  noResults: {
    textAlign: "center",
    padding: "60px 20px",
    color: "rgba(232,224,208,0.5)",
    fontSize: "18px",
  },
  detailBtn: {
    background: "#C9A84C",
    color: "#07101e",
    border: "none",
    borderRadius: "7px",
    padding: "8px 16px",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.3s ease",
  },

  // ── WHO WE ARE ──
  whoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "clamp(32px, 6vw, 72px)",
    alignItems: "center",
  },
  whoImageWrap: {
    position: "relative",
  },
  whoImg: {
    width: "100%",
    height: "auto",
    maxHeight: "480px",
    objectFit: "cover",
    display: "block",
    borderRadius: "4px",
  },
  whoCornerTopRight: {
    position: "absolute",
    top: "-14px",
    right: "-14px",
    width: "80px",
    height: "80px",
    border: "2px solid #C9A84C",
    borderRadius: "2px",
    zIndex: 0,
  },
  whoCornerBottomLeft: {
    position: "absolute",
    bottom: "-14px",
    left: "-14px",
    width: "80px",
    height: "80px",
    border: "2px solid rgba(201,168,76,0.35)",
    borderRadius: "2px",
  },
  whoBadge: {
    position: "absolute",
    top: "24px",
    left: "-20px",
    background: "#C9A84C",
    padding: "16px 20px",
    zIndex: 2,
  },
  whoBadgeNum: {
    display: "block",
    fontFamily: "Georgia, serif",
    fontSize: "42px",
    fontWeight: 700,
    color: "#07101e",
    lineHeight: 1,
  },
  whoBadgeLabel: {
    display: "block",
    fontSize: "10px",
    fontWeight: 700,
    color: "rgba(7,16,30,0.7)",
    letterSpacing: "1.5px",
    marginTop: "4px",
    lineHeight: 1.4,
  },
  whoRight: {
    paddingLeft: "8px",
  },
  whoEyebrowRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
  },
  whoEyebrowLine: {
    display: "inline-block",
    width: "32px",
    height: "1.5px",
    background: "#C9A84C",
    flexShrink: 0,
  },
  whoEyebrowText: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#C9A84C",
    letterSpacing: "3px",
  },
  whoHeading: {
    fontFamily: "Georgia, serif",
    fontSize: "clamp(28px,3.8vw,42px)",
    fontWeight: 700,
    color: "#f0e8d8",
    letterSpacing: "-1px",
    lineHeight: 1.15,
    margin: "0 0 24px",
  },
  whoBody: {
    fontSize: "15px",
    color: "rgba(232,224,208,0.58)",
    lineHeight: 1.8,
    marginBottom: "18px",
  },

  // ── LOCATIONS ──
  locationsHead: {
    marginBottom: "40px",
  },
  locationsTitle: {
    fontFamily: "Georgia, serif",
    fontSize: "clamp(28px,5vw,52px)",
    fontWeight: 700,
    color: "#f0e8d8",
    letterSpacing: "-1.5px",
    lineHeight: 1.1,
    margin: 0,
  },
  locGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "6px",
  },
  locCardFeatured: {
    position: "relative",
    overflow: "hidden",
    borderRadius: "4px",
    cursor: "pointer",
    gridRow: "1 / 3",
  },
  locRight: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "6px",
  },
  locCard: {
    position: "relative",
    overflow: "hidden",
    borderRadius: "4px",
    cursor: "pointer",
    height: "240px",
  },
  locImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  locOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 55%, transparent 100%)",
  },
  locLabel: {
    position: "absolute",
    bottom: "20px",
    left: "22px",
  },
  locName: {
    fontFamily: "Georgia, serif",
    fontSize: "20px",
    fontWeight: 700,
    color: "#fff",
    margin: "0 0 4px",
    letterSpacing: "-0.3px",
  },
  locCount: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#C9A84C",
    letterSpacing: "1.5px",
    margin: 0,
  },

  // ── PROCESS ──
  processGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))",
    marginTop: "48px",
    borderTop: "1px solid rgba(201,168,76,0.15)",
  },
  processStep: {
    padding: "32px 28px",
    borderRight: "1px solid rgba(201,168,76,0.15)",
    borderBottom: "1px solid rgba(201,168,76,0.15)",
  },
  stepNum: {
    fontFamily: "Georgia, serif",
    fontSize: "38px",
    fontWeight: 700,
    color: "rgba(201,168,76,0.3)",
    display: "block",
    marginBottom: "12px",
    letterSpacing: "-1px",
  },
  stepTitle: {
    fontSize: "17px",
    fontWeight: 700,
    color: "#f0e8d8",
    margin: "0 0 8px",
  },
  stepDesc: {
    fontSize: "14px",
    color: "rgba(232,224,208,0.5)",
    lineHeight: 1.65,
    margin: 0,
  },

  // ── WHY US ──
  whyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "clamp(32px, 5vw, 80px)",
    alignItems: "center",
  },
  whyBody: {
    color: "rgba(232,224,208,0.55)",
    lineHeight: 1.8,
    fontSize: "15px",
    margin: "20px 0 32px",
  },

  // ── TESTIMONIALS ──
  testiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
    gap: "24px",
    marginTop: "40px",
  },
  testiCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(201,168,76,0.15)",
    borderRadius: "14px",
    padding: "28px",
  },

  // ── CTA ──
  ctaBanner: {
    background: "#07101e",
    borderTop: "1px solid rgba(201,168,76,0.2)",
    padding: "clamp(56px,8vw,88px) 32px",
    textAlign: "center",
  },
  ctaInner: {
    maxWidth: "640px",
    margin: "0 auto",
  },
  ctaTitle: {
    fontFamily: "Georgia, serif",
    fontSize: "clamp(28px,4.5vw,46px)",
    color: "#f0e8d8",
    fontWeight: 700,
    margin: "0 0 16px",
    letterSpacing: "-1.5px",
  },
  ctaSub: {
    color: "rgba(232,224,208,0.55)",
    fontSize: "16px",
    marginBottom: "36px",
    lineHeight: 1.65,
  },
  goldBtn: {
    background: "#C9A84C",
    color: "#07101e",
    border: "none",
    borderRadius: "4px",
    padding: "14px 30px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "1px",
    textTransform: "uppercase",
  },
  outlineBtn: {
    background: "transparent",
    color: "rgba(232,224,208,0.8)",
    border: "2px solid rgba(232,224,208,0.25)",
    borderRadius: "4px",
    padding: "12px 28px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    letterSpacing: "0.5px",
  },
};

// ─── ADD ANIMATION AND RESPONSIVE STYLES ──
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes tickerMove { 
      0% { transform: translateX(0); } 
      100% { transform: translateX(-50%); } 
    }

    /* ── RESPONSIVE BREAKPOINTS ── */
    
    @media (max-width: 1024px) {
      .searchRow {
        flex-direction: row !important;
        flex-wrap: wrap !important;
      }
      .searchSelect {
        flex: 1;
        min-width: 140px;
      }
      .propGrid {
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)) !important;
      }
      .locGrid {
        grid-template-columns: 1fr !important;
        gap: 12px !important;
      }
      .locRight {
        grid-template-columns: 1fr 1fr !important;
        gap: 12px !important;
      }
      .locCardFeatured {
        grid-row: auto !important;
        height: 300px !important;
      }
      .locCard {
        height: 200px !important;
      }
      .whoGrid {
        grid-template-columns: 1fr !important;
        gap: 40px !important;
      }
      .whoImageWrap {
        max-width: 600px;
        margin: 0 auto;
      }
      .whyGrid {
        grid-template-columns: 1fr !important;
        gap: 48px !important;
      }
      .processGrid {
        grid-template-columns: repeat(2, 1fr) !important;
      }
      .processStep {
        border-right: none !important;
      }
      .testiGrid {
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)) !important;
      }
    }

    @media (max-width: 768px) {
      .heroContent {
        padding: 100px 24px 60px !important;
      }
      .heroHeadline {
        font-size: clamp(40px, 8vw, 56px) !important;
      }
      .searchRow {
        flex-direction: column !important;
        align-items: stretch !important;
        padding: 16px !important;
        gap: 8px !important;
      }
      .searchInput {
        padding: 12px 0 !important;
        font-size: 14px !important;
      }
      .searchSelect {
        width: 100% !important;
        padding: 10px 12px !important;
        font-size: 13px !important;
      }
      .searchBtn {
        width: 100% !important;
        justify-content: center !important;
        padding: 12px !important;
      }
      .searchTabs .searchTab {
        font-size: 13px !important;
        padding: 12px 0 !important;
      }
      .heroStats {
        gap: 24px !important;
        margin-top: 32px !important;
      }
      .heroStat {
        flex: 1 1 40% !important;
      }
      .section {
        padding: 48px 0 !important;
      }
      .inner {
        padding: 0 20px !important;
      }
      .sectionHead {
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 16px !important;
        margin-bottom: 32px !important;
      }
      .filterRow {
        width: 100% !important;
        gap: 6px !important;
      }
      .filterBtn {
        font-size: 12px !important;
        padding: 6px 14px !important;
      }
      .propGrid {
        grid-template-columns: 1fr !important;
        gap: 20px !important;
      }
      .locRight {
        grid-template-columns: 1fr !important;
        gap: 12px !important;
      }
      .locCard {
        height: 180px !important;
      }
      .locCardFeatured {
        height: 250px !important;
      }
      .processGrid {
        grid-template-columns: 1fr !important;
      }
      .processStep {
        padding: 24px 20px !important;
        border-bottom: 1px solid rgba(201,168,76,0.15) !important;
      }
      .testiGrid {
        grid-template-columns: 1fr !important;
        gap: 16px !important;
      }
      .testiCard {
        padding: 20px !important;
      }
      .whoBadge {
        left: -12px !important;
        padding: 12px 16px !important;
      }
      .whoBadgeNum {
        font-size: 32px !important;
      }
      .whoCornerTopRight,
      .whoCornerBottomLeft {
        display: none !important;
      }
      .whoImg {
        max-height: 350px !important;
      }
      .ctaBanner {
        padding: 48px 20px !important;
      }
      .goldBtn, .outlineBtn {
        width: 100% !important;
        justify-content: center !important;
        text-align: center !important;
      }
      .ctaInner .goldBtn, 
      .ctaInner .outlineBtn {
        width: 100% !important;
      }
    }

    @media (max-width: 480px) {
      .heroContent {
        padding: 80px 16px 40px !important;
      }
      .heroEyebrow {
        font-size: 10px !important;
        letter-spacing: 2px !important;
      }
      .heroHeadline {
        font-size: clamp(32px, 9vw, 42px) !important;
        letter-spacing: -1px !important;
      }
      .heroSub {
        font-size: 14px !important;
        margin-bottom: 28px !important;
      }
      .searchBox {
        border-radius: 10px !important;
      }
      .searchTabs .searchTab {
        font-size: 12px !important;
        padding: 10px 0 !important;
      }
      .searchRow {
        padding: 12px !important;
        gap: 6px !important;
      }
      .searchInput {
        font-size: 13px !important;
        padding: 8px 0 !important;
        min-width: auto !important;
      }
      .searchSelect {
        font-size: 12px !important;
        padding: 8px 10px !important;
      }
      .searchBtn {
        font-size: 12px !important;
        padding: 10px !important;
      }
      .heroStats {
        gap: 16px !important;
        margin-top: 24px !important;
      }
      .heroStatVal {
        font-size: 20px !important;
      }
      .heroStatLabel {
        font-size: 9px !important;
        letter-spacing: 1px !important;
      }
      .section {
        padding: 32px 0 !important;
      }
      .inner {
        padding: 0 16px !important;
      }
      .eyebrow {
        font-size: 10px !important;
        letter-spacing: 2px !important;
      }
      .sectionTitle {
        font-size: clamp(24px, 6vw, 30px) !important;
      }
      .filterBtn {
        font-size: 11px !important;
        padding: 5px 12px !important;
      }
      .locName {
        font-size: 17px !important;
      }
      .locCount {
        font-size: 10px !important;
      }
      .whoHeading {
        font-size: clamp(24px, 5vw, 30px) !important;
      }
      .whoBody {
        font-size: 14px !important;
      }
      .locationsTitle {
        font-size: clamp(24px, 6vw, 32px) !important;
      }
      .stepNum {
        font-size: 30px !important;
      }
      .stepTitle {
        font-size: 15px !important;
      }
      .stepDesc {
        font-size: 13px !important;
      }
      .ctaTitle {
        font-size: clamp(24px, 6vw, 32px) !important;
      }
      .ctaSub {
        font-size: 14px !important;
        margin-bottom: 24px !important;
      }
      .goldBtn, .outlineBtn {
        font-size: 12px !important;
        padding: 12px 20px !important;
      }
      .tickerItem {
        font-size: 10px !important;
        padding: 0 20px !important;
      }
    }

    @media (max-width: 360px) {
      .heroContent {
        padding: 60px 12px 32px !important;
      }
      .heroHeadline {
        font-size: clamp(28px, 8vw, 34px) !important;
      }
      .heroSub {
        font-size: 13px !important;
      }
      .inner {
        padding: 0 12px !important;
      }
      .searchTabs .searchTab {
        font-size: 11px !important;
        padding: 8px 0 !important;
      }
      .searchSelect {
        font-size: 11px !important;
        padding: 6px 8px !important;
      }
      .filterBtn {
        font-size: 10px !important;
        padding: 4px 10px !important;
      }
      .locCard {
        height: 150px !important;
      }
      .locCardFeatured {
        height: 200px !important;
      }
      .whoBadge {
        padding: 8px 12px !important;
      }
      .whoBadgeNum {
        font-size: 26px !important;
      }
      .whoBadgeLabel {
        font-size: 8px !important;
      }
      .tickerItem {
        font-size: 9px !important;
        padding: 0 14px !important;
      }
    }

    /* Landscape Phone Optimization */
    @media (max-height: 500px) and (orientation: landscape) {
      .hero {
        min-height: 120vh !important;
      }
      .heroContent {
        padding: 80px 24px 40px !important;
      }
      .heroHeadline {
        font-size: clamp(32px, 5vw, 48px) !important;
      }
      .heroSub {
        font-size: 13px !important;
        margin-bottom: 20px !important;
      }
      .heroStats {
        margin-top: 20px !important;
        gap: 16px !important;
      }
      .searchBox {
        max-width: 700px !important;
      }
      .searchRow {
        flex-direction: row !important;
        flex-wrap: wrap !important;
        gap: 6px !important;
      }
      .searchSelect {
        flex: 1 !important;
        min-width: 100px !important;
      }
      .searchBtn {
        flex: 0 1 auto !important;
        padding: 8px 20px !important;
      }
    }

    /* Touch Device Optimizations */
    @media (hover: none) {
      .propCard:hover,
      .locCard:hover,
      .locCardFeatured:hover,
      .testiCard:hover {
        transform: none !important;
      }
      .propCard:active,
      .locCard:active,
      .locCardFeatured:active {
        transform: scale(0.98) !important;
      }
      .filterBtn:active {
        opacity: 0.7;
      }
      .goldBtn:active,
      .outlineBtn:active,
      .searchBtn:active {
        transform: scale(0.96);
      }
    }

    /* Reduced Motion */
    @media (prefers-reduced-motion: reduce) {
      *,
      *::before,
      *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
      .tickerTrack {
        animation: none !important;
      }
    }

    /* Print Styles */
    @media print {
      .hero {
        min-height: 50vh !important;
      }
      .heroOverlay {
        background: rgba(0,0,0,0.3) !important;
      }
      .searchBox,
      .ticker,
      .ctaBanner {
        display: none !important;
      }
      .propCard {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
        border: 1px solid #333 !important;
      }
      .section {
        padding: 32px 0 !important;
      }
      .goldBtn,
      .outlineBtn,
      .detailBtn {
        background: #333 !important;
        color: #fff !important;
        border: 1px solid #666 !important;
      }
    }
  `;
  document.head.appendChild(styleSheet);
}
